import {
  deleteTemplateFragmentRequest,
  fetchTemplateFragmentUsageRequest,
} from '@/services/api/emailCenter';
import {
  TemplateFragmentModel,
  TemplateFragmentUsageModel,
  TemplateFragmentUsageResultModel,
} from '@/typings/api/emailCenter';
import { zIndexAboveDrawers } from '@/utils/dom';
import { F, getIntlTranslation } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import { Modal, Typography } from 'antd';
import React, { useCallback, useState } from 'react';
import { useIntl } from 'umi';
import { fragmentDisplayName } from '../../components/EmailBodyEditor/labels';
import { decideFragmentDeletion } from './fragmentDeletion';

import styles from './FragmentsSection.less';

/** One line of "what would break": the thing, and how it uses the block. */
const UsageLine: React.FC<{ usage: TemplateFragmentUsageModel }> = ({
  usage,
}) => {
  if (usage.type === 'default_layout') {
    return (
      <F
        id="pages.emailCenter.fragments.usage.defaultLayout"
        defaultMessage="Your default layout — it wraps every email that doesn't pick one of its own."
      />
    );
  }

  if (usage.type === 'fragment') {
    return usage.fragment_type === 'layout' ? (
      <F
        id="pages.emailCenter.fragments.usage.layout"
        defaultMessage="The layout “{name}”"
        values={{ name: fragmentDisplayName(usage) }}
      />
    ) : (
      <F
        id="pages.emailCenter.fragments.usage.partial"
        defaultMessage="The building block “{name}”"
        values={{ name: fragmentDisplayName(usage) }}
      />
    );
  }

  return (
    <>
      <F
        id="pages.emailCenter.fragments.usage.template"
        defaultMessage="The email “{name}”"
        values={{ name: usage.name }}
      />
      {usage.via.includes('layout') && (
        <>
          {' '}
          <Typography.Text type="secondary">
            <F
              id="pages.emailCenter.fragments.usage.asFrame"
              defaultMessage="(as its layout)"
            />
          </Typography.Text>
        </>
      )}
      {usage.via.includes('draft') && !usage.via.includes('body') && (
        <>
          {' '}
          <Typography.Text type="secondary">
            <F
              id="pages.emailCenter.fragments.usage.inDraft"
              defaultMessage="(in an unpublished draft)"
            />
          </Typography.Text>
        </>
      )}
    </>
  );
};

const UsageList: React.FC<{ usages: TemplateFragmentUsageModel[] }> = ({
  usages,
}) => (
  <ul className={styles.usageList}>
    {usages.map((usage, index) => (
      <li key={index}>
        <UsageLine usage={usage} />
      </li>
    ))}
  </ul>
);

/**
 * Deleting a fragment, with the "what would break" check in front of it.
 *
 * Shared by the catalog rows and the editor's own Delete so both apply the same
 * rule and say the same things — see {@link decideFragmentDeletion} for why a
 * block in use is not necessarily a block you may not delete.
 *
 * The server checks again on the DELETE (409), so this is about telling you
 * *before* you press it, not about being the guard.
 *
 * The dialogs come from `Modal.useModal()` rather than the static
 * `Modal.confirm`: the static call renders in a React root of its own, outside
 * this app's providers, and the content below is not context-free — `F`
 * resolves its text through `useIntl()`, which throws without an
 * `IntlProvider` in the ancestry, and a throw there means no dialog at all.
 * The hook's `contextHolder` renders inside the tree; the caller mounts it.
 */
export function useFragmentDeletion({
  onDeleted,
}: { onDeleted?: (record: TemplateFragmentModel) => void } = {}) {
  const intl = useIntl();
  const [modal, contextHolder] = Modal.useModal();
  // One id, not a boolean: rows share the hook, and only the row acted on
  // should look busy.
  const [busyId, setBusyId] = useState<number | null>(null);

  const performDelete = useCallback(
    async (record: TemplateFragmentModel) => {
      setBusyId(record.id);
      const result = await deleteTemplateFragmentRequest({
        params: { fragmentId: record.id },
      });
      setBusyId(null);

      processAPIResponseNow(result, {
        successMessage: getIntlTranslation(intl, {
          id: 'pages.emailCenter.fragments.deleted',
          defaultMessage: 'Fragment deleted.',
        }),
        onSuccess: () => onDeleted?.(record),
        // The 409 carries its own explanation (the pre-flight missed something,
        // or the catalog moved on since) — show that rather than a generic one.
        showErrorMessage: true,
      });
    },
    [intl, onDeleted],
  );

  const requestDelete = useCallback(
    async (record: TemplateFragmentModel) => {
      setBusyId(record.id);
      const result = await fetchTemplateFragmentUsageRequest({
        params: { fragmentId: record.id },
      });
      setBusyId(null);

      const found: { usage: TemplateFragmentUsageResultModel | null } = {
        usage: null,
      };
      processAPIResponseNow(
        result,
        {
          onSuccess: (data) => {
            found.usage = data ?? null;
          },
          // Swallowed on purpose: the user asked to delete, not to run a
          // lookup, and the delete is still offered (the server runs the same
          // check on it). The dialog says the check could not be read instead.
          onError: () => {},
        },
        'data',
      );

      const decision = decideFragmentDeletion(found.usage);
      // Or the dialog opens behind the editor drawer that raised it.
      const zIndex = zIndexAboveDrawers();

      if (decision.blocked) {
        // `okCancel: false` is a one-button Modal.confirm — the blocking
        // confirmation docs/drawers.md allows, with nothing to confirm.
        modal.confirm({
          okCancel: false,
          zIndex,
          title: getIntlTranslation(intl, {
            id: 'pages.emailCenter.fragments.blockedTitle',
            defaultMessage: 'Still in use',
          }),
          content: (
            <div>
              <Typography.Paragraph>
                <F
                  id="pages.emailCenter.fragments.blockedHint"
                  defaultMessage="This block has no standard version to fall back on, so deleting it would leave these pointing at nothing. Change them over first, then delete it."
                />
              </Typography.Paragraph>
              <UsageList usages={decision.usages} />
            </div>
          ),
          okText: getIntlTranslation(intl, {
            id: 'common.actions.got_it',
            defaultMessage: 'Got it',
          }),
        });
        return;
      }

      modal.confirm({
        zIndex,
        okButtonProps: { danger: true },
        okText: getIntlTranslation(intl, {
          id: 'common.actions.delete',
          defaultMessage: 'Delete',
        }),
        cancelText: getIntlTranslation(intl, {
          id: 'common.actions.cancel',
          defaultMessage: 'Cancel',
        }),
        title: decision.restoresStandard
          ? getIntlTranslation(intl, {
              id: 'pages.emailCenter.fragments.restoreTitle',
              defaultMessage: 'Back to the standard version?',
            })
          : getIntlTranslation(intl, {
              id: 'pages.emailCenter.fragments.deleteTitle',
              defaultMessage: 'Delete this block?',
            }),
        content: (
          <div>
            <Typography.Paragraph>
              {decision.restoresStandard ? (
                <F
                  id="pages.emailCenter.fragments.restoreHint"
                  defaultMessage="Your own version goes away and the standard block takes over again everywhere it is used."
                />
              ) : decision.usages.length > 0 ? (
                <F
                  id="pages.emailCenter.fragments.deleteUsedHint"
                  defaultMessage="Nothing else falls back on this key, so these stop working — check them first:"
                />
              ) : (
                <F
                  id="pages.emailCenter.fragments.deleteUnusedHint"
                  defaultMessage="Nothing references this block, so no email changes."
                />
              )}
            </Typography.Paragraph>
            {decision.usages.length > 0 && (
              <UsageList usages={decision.usages} />
            )}
            {decision.unverified && (
              <Typography.Paragraph type="secondary">
                <F
                  id="pages.emailCenter.fragments.usageUnknown"
                  defaultMessage="What uses this block could not be checked just now — the delete is refused if it turns out to be in use."
                />
              </Typography.Paragraph>
            )}
          </div>
        ),
        onOk: () => performDelete(record),
      });
    },
    [intl, modal, performDelete],
  );

  return { requestDelete, busyId, contextHolder };
}
